import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';

import type { AdminProgressSummary, ProgressSummary } from './tierConstants';
import {
  PROFILE_SUMMARY_KEY,
  HISTORY_KEY,
  ADMIN_AGENT_PROFILE_KEY,
  ADMIN_AGENTS_PROGRESS_OVERVIEW_KEY,
} from './progressKeys';

export type HistoryRow = {
  periodYear: number;
  periodMonth: number;
  totalScreenings: number;
  primaryScreenings: number;
  subScreenings: number;
  tierReached: ProgressSummary['monthly']['tierAchievedThisMonth'];
  workingDaysInPeriod: number;
  daysAtOrAbove25: number;
  longestStreak: number;
  isCurrentMonth: boolean;
};

export type AdminAgentRow = {
  agentId: number;
  displayName: string;
  role: 'agent';
  isActive: boolean;
  todayCount: number;
  screeningsMonth: number;
  tierAchievedThisMonth: ProgressSummary['monthly']['tierAchievedThisMonth'];
  tierMaintained: ProgressSummary['monthly']['tierMaintained'];
  tierDisplayed: ProgressSummary['monthly']['tierDisplayed'];
  streakDays: number;
  trendVsLastMonth: 'up' | 'flat' | 'down' | 'n/a';
};

export type AdminOverviewResponse = {
  asOf: string;
  currentPeriod: { year: number; month: number };
  tierDistribution: {
    t3: number;
    t2: number;
    t1: number;
    base: number;
    below_floor: number;
  };
  agents: AdminAgentRow[];
};

export function useProfileSummary() {
  return useQuery<ProgressSummary>({
    queryKey: PROFILE_SUMMARY_KEY,
    queryFn: () => apiCall<ProgressSummary>('/agents/me/profile-summary'),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useAgentHistory(months: number = 12) {
  return useQuery<HistoryRow[]>({
    queryKey: HISTORY_KEY(months),
    queryFn: () => apiCall<HistoryRow[]>(`/agents/me/history?months=${months}`),
    staleTime: 300_000,
  });
}

export function useAdminAgentProgress(
  agentId: number | undefined,
  opts: { enabled?: boolean } = {},
) {
  // F5-R8c — el endpoint admin trae además `series` + `activeDaysMonth`
  // (aditivo); el tipo lo refleja y los campos viejos no cambian.
  return useQuery<AdminProgressSummary>({
    queryKey: ADMIN_AGENT_PROFILE_KEY(agentId ?? -1),
    queryFn: () => apiCall<AdminProgressSummary>(`/admin/agents/${agentId}/profile-summary`),
    enabled: opts.enabled !== false && agentId != null && agentId > 0,
    staleTime: 60_000,
  });
}

export function useAdminAgentsProgressOverview() {
  return useQuery<AdminOverviewResponse>({
    queryKey: ADMIN_AGENTS_PROGRESS_OVERVIEW_KEY,
    queryFn: () => apiCall<AdminOverviewResponse>('/admin/agents-progress-overview'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}
